import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { useNotices } from '../notices';
import { useSession } from '../session';
import { HELP_HOME, helpUrl } from '../links';
import { ThemeToggle } from './ThemeToggle';
import './topbar.css';

/** Schließt ein Menü bei Klick daneben, mit Escape und bei jedem Seitenwechsel */
function useDismiss(open: boolean, close: () => void, root: React.RefObject<HTMLElement | null>) {
  const location = useLocation();
  useEffect(() => close(), [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open, close, root]);
}

function Menu({ id, trigger, children, className = '' }: { id: string; trigger: (props: { open: boolean; toggle: () => void }) => ReactNode; children: (close: () => void) => ReactNode; className?: string }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const close = () => setOpen(false);
  useDismiss(open, close, root);
  return (
    <div ref={root} className={`tb-menu ${className}`.trim()} id={id}>
      {trigger({ open, toggle: () => setOpen((v) => !v) })}
      {open && <div className="tb-panel">{children(close)}</div>}
    </div>
  );
}

/**
 * Welche Hinweise der Benutzer schon gelesen hat. Ein Hinweis zählt als gelesen, solange sein Text gleich bleibt; ändert sich die Zahl, ist er wieder neu.
 * Der Zustand liegt nur in diesem Browser (je Benutzer). Ohne Speicher gilt er bis zum Neuladen.
 */
function useReadNotices(userId: string | undefined) {
  const storageKey = `kontor:notices-read:${userId ?? ''}`;
  const [read, setRead] = useState<Record<string, string>>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? (JSON.parse(raw) as Record<string, string>) : {};
    } catch {
      return {};
    }
  });
  const save = (next: Record<string, string>) => {
    setRead(next);
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      // ohne Speicher gilt die Markierung nur bis zum Neuladen
    }
  };
  return { read, save };
}

/**
 * Glocke in der Kopfleiste: zeigt, wie viele ungelesene Hinweise gerade Aufmerksamkeit brauchen, und öffnet beim Klick die Liste; jeder Hinweis lässt sich als gelesen markieren. Die Hinweise sind dieselben wie auf der Übersicht (überfällige
 * Aufgaben und Rechnungen, fällige Wiedervorlagen, Aufträge zur Planung, neue Anfragen …), jeder führt zur passenden Seite.
 */
/**
 * Hilfe zur aktuellen Seite: Das Fragezeichen öffnet die passende Anleitung auf der Webseite in einem neuen Tab. Auf dem Handy fehlt es in der Kopfleiste,
 * dort stehen die Einträge im Benutzermenü.
 */
export function HelpLink() {
  const { pathname } = useLocation();
  return (
    <a className="tb-icon tb-help" href={helpUrl(pathname)} target="_blank" rel="noopener noreferrer" aria-label="Hilfe zu dieser Seite (öffnet die Anleitung in einem neuen Tab)" title="Hilfe zu dieser Seite">
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M9.5 9.2a2.6 2.6 0 0 1 5 .9c0 1.7-2.5 2.1-2.5 3.7M12 17.2h.01" />
      </svg>
    </a>
  );
}

export function NotificationBell() {
  const { session } = useSession();
  const { notices } = useNotices();
  const { read, save } = useReadNotices(session?.user.id);
  const [showRead, setShowRead] = useState(false);
  const mark = (n: { text: string; stamp?: string }) => (n.stamp ? `${n.text}|${n.stamp}` : n.text);
  const isRead = (n: { key: string; text: string; stamp?: string }) => read[n.key] === mark(n);
  const unread = notices.filter((n) => !isRead(n));
  const done = notices.filter(isRead);
  const alerts = unread.filter((n) => n.tone === 'alert').length;
  const label = unread.length === 0 ? 'Hinweise: keine ungelesenen' : `Hinweise: ${unread.length} ungelesen${alerts > 0 ? `, davon ${alerts} dringend` : ''}`;
  const markRead = (n: { key: string; text: string; stamp?: string }) => save({ ...read, [n.key]: mark(n) });
  const markUnread = (n: { key: string }) => {
    const rest = { ...read };
    delete rest[n.key];
    save(rest);
  };
  const shown = showRead ? notices : unread;
  return (
    <Menu
      id="notice-menu"
      trigger={({ open, toggle }) => (
        <button type="button" className="tb-icon" aria-label={label} aria-haspopup="true" aria-expanded={open} aria-controls="notice-panel" onClick={toggle}>
          <svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
            <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
          </svg>
          {unread.length > 0 && <span className={`tb-badge${alerts > 0 ? ' tb-badge--alert' : ''}`} aria-hidden="true">{unread.length > 9 ? '9+' : unread.length}</span>}
        </button>
      )}
    >
      {(close) => (
        <div id="notice-panel" role="region" aria-label="Hinweise">
          <div className="tb-panel__head">
            <strong>Hinweise</strong>
            {unread.length > 0 ? (
              <button type="button" className="tb-link" onClick={() => save({ ...read, ...Object.fromEntries(unread.map((n) => [n.key, mark(n)])) })}>Alle als gelesen markieren</button>
            ) : (
              <span className="muted small">alles gelesen</span>
            )}
          </div>
          {shown.length === 0 ? (
            <p className="tb-empty">{notices.length === 0 ? 'Im Moment braucht nichts Ihre Aufmerksamkeit.' : 'Keine ungelesenen Hinweise.'}</p>
          ) : (
            <ul className="tb-notices">
              {shown.map((n) => {
                const was = isRead(n);
                return (
                  <li key={n.key} className={`tb-notices__item${was ? ' is-read' : ''}`}>
                    <Link to={n.to} className={`tb-notice tb-notice--${n.tone}`} onClick={() => { if (!was) markRead(n); close(); }}>
                      <span className="tb-notice__dot" aria-hidden="true" />
                      <span>{n.text}</span>
                    </Link>
                    <button type="button" className="tb-notices__mark" aria-label={was ? 'Als ungelesen markieren' : 'Als gelesen markieren'} title={was ? 'Als ungelesen markieren' : 'Als gelesen markieren'} onClick={() => (was ? markUnread(n) : markRead(n))}>
                      {was ? '↺' : '✓'}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="tb-panel__foot">
            <Link to="/" onClick={close}>Zur Übersicht</Link>
            {done.length > 0 && <button type="button" className="tb-link" onClick={() => setShowRead((v) => !v)}>{showRead ? 'Gelesene ausblenden' : `Gelesene anzeigen (${done.length})`}</button>}
          </div>
        </div>
      )}
    </Menu>
  );
}

/** Profilfoto aus dem Mitarbeiterprofil des Benutzers, sonst der Anfangsbuchstabe seines Namens */
export function UserAvatar({ size = 34 }: { size?: number }) {
  const { session } = useSession();
  const me = useQuery({
    queryKey: ['me-employee'],
    queryFn: async () => (await api<{ data: { photoVersion: string | null } | null }>('GET', '/me/employee')).data,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const initial = session?.user.name.trim().charAt(0).toUpperCase() ?? '';
  const version = me.data?.photoVersion;
  const style = { width: size, height: size };
  return version ? <img className="tb-avatar tb-avatar--photo" src={`/api/v1/me/employee/photo?v=${version}`} alt="" style={style} /> : <span className="tb-avatar" style={style} aria-hidden="true">{initial}</span>;
}

/**
 * Benutzermenü in der Kopfleiste: Foto oder Anfangsbuchstabe mit Name und Rolle, beim Klick ein Menü mit „Mein Konto“, dem Umschalter zwischen hellem und dunklem Design und
 * „Abmelden“.
 */
export function UserMenu() {
  const { session, logout } = useSession();
  const navigate = useNavigate();
  if (!session) return null;
  return (
    <Menu
      id="user-menu"
      className="tb-menu--user"
      trigger={({ open, toggle }) => (
        <button type="button" className="tb-user" aria-label={`Benutzermenü von ${session.user.name}`} aria-haspopup="menu" aria-expanded={open} aria-controls="user-panel" onClick={toggle}>
          <UserAvatar />
          <span className="tb-user__who">
            <span className="tb-user__name">{session.user.name}</span>
            <small>{session.user.role.name}</small>
          </span>
          <svg className="tb-user__chevron" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
      )}
    >
      {(close) => (
        <div id="user-panel" role="menu" aria-label="Benutzermenü">
          <div className="tb-panel__who">
            <UserAvatar size={40} />
            <span>
              <strong>{session.user.name}</strong>
              <small>{session.user.role.name}</small>
            </span>
            <ThemeToggle variant="icon" />
          </div>
          <NavLink to="/konto" role="menuitem" className="tb-item" onClick={close}>Mein Konto</NavLink>
          <a role="menuitem" className="tb-item" href={HELP_HOME} target="_blank" rel="noopener noreferrer" onClick={close}>Anleitung und Suche</a>
          <button
            type="button"
            role="menuitem"
            className="tb-item"
            onClick={async () => {
              close();
              await logout();
              navigate('/login', { replace: true });
            }}
          >
            Abmelden
          </button>
        </div>
      )}
    </Menu>
  );
}
